import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Grid,
  GridItem,
  Select,
  Input,
} from "@chakra-ui/react";
import { format } from "date-fns";

import { formatCurrency } from "@/utils/currency";
import {
  MONTHS_OPTION,
  COLORS,
  INVOICE_STATUS,
  CURRENCY_TO_LOCALE_STRING,
} from "@/common/constants";
import { SireReviewInvoice } from "@/types/sireReviewInvoice.interface";
import { Form, Formik } from "formik";

export type SireReviewerInvoiceDataConfirmation = {
  invoiceId: string; // internal UID
  invoiceNumber: string; // user-facing
  companyName: string;
  month: number;
  year: number;
  invoiceDate: Date | string;
  totalReviews: number;
  currency: string; // "IDR" / "USD"
  totalAmount: number;
};

type ModalSireReviewerInvoiceConfirmProps = {
  isOpen: boolean;
  onClose: () => void;
  handleCancel: () => void;
	handleConfirm: (approvalStatus: string) => Promise<void>;
  dataConfirmation: SireReviewInvoice | undefined;
  approvalStatus: string; // of number
  handleChangeApprovalStatus: (status: string) => void; // string of number, can easily be converted to number
  handleChangeNote: (note: string) => void;
  errorMessageApproval: string;
  errorMessageNote: string;
};

const ModalSireReviewerInvoiceConfirm = ({
  isOpen,
  onClose,
  handleCancel,
  handleConfirm,
  approvalStatus,
  dataConfirmation,
  handleChangeApprovalStatus,
  handleChangeNote,
  errorMessageApproval,
  errorMessageNote,
}: ModalSireReviewerInvoiceConfirmProps) => {
  return (
    <Modal onClose={onClose} isOpen={isOpen} isCentered size="xl">
      <ModalOverlay />
			<Formik
				initialValues={{ status: "" }}
				onSubmit={async (values) => {
					await handleConfirm(values.status)
					handleChangeApprovalStatus(values.status)
				}}>
				{formik => (
					<Form>
      <ModalContent borderRadius={15} padding={"10px"}>
        <ModalHeader>Invoice Approval</ModalHeader>
        <ModalCloseButton
          onClick={handleCancel}
          _focus={{ outline: "none", boxShadow: "none" }}
							/>
        <ModalBody marginTop={5}>
          <Grid templateColumns="repeat(2, 1fr)" gap={6}>
            <GridItem w="100%">
              <p style={{ fontWeight: "bold" }}>Reviewer Company</p>
              <p>{dataConfirmation?.inspectionCompanyName ?? "-"}</p>
            </GridItem>
            <GridItem w="100%">
              <p style={{ fontWeight: "bold" }}>Review Month</p>
              <p>
                {dataConfirmation
                  ? MONTHS_OPTION[dataConfirmation.reviewMonth] +
												" " +
												dataConfirmation.reviewYear
                  : "-"}
              </p>
            </GridItem>
            <GridItem w="100%">
              <p style={{ fontWeight: "bold" }}>Invoice ID</p>
              <p>{dataConfirmation?.invoiceNumber ?? "-"}</p>
            </GridItem>
            <GridItem w="100%">
              <p style={{ fontWeight: "bold" }}>Invoice Date</p>
              <p>
                {dataConfirmation?.invoiceDate
                  ? format(dataConfirmation?.invoiceDate, "dd-MM-yyyy")
                  : "-"}
              </p>
            </GridItem>
            <GridItem w="100%">
              <p style={{ fontWeight: "bold" }}>Total Reviews</p>
              <p>{dataConfirmation?.totalReview ?? "-"}</p>
            </GridItem>
            <GridItem w="100%">
              <p style={{ fontWeight: "bold" }}>Amount</p>
              <p>
                {dataConfirmation && dataConfirmation.currency
                  ? formatCurrency(
										dataConfirmation.totalAmount,
										dataConfirmation.currency,
										CURRENCY_TO_LOCALE_STRING[dataConfirmation.currency],
									)
                  : "-"}
              </p>
            </GridItem>
          </Grid>
          <Select
									name="status"
            fontSize="12px"
            cursor="pointer"
            marginTop={10}
            marginBottom={5}
									onChange={formik.handleChange}
            borderColor={errorMessageApproval ? "red" : "gray"}
								>
            <option key="none" value={-1}>
              Please select...
            </option>
            <option key="approved" value={INVOICE_STATUS.APPROVED}>
              Approve
            </option>
            <option key="rejected" value={INVOICE_STATUS.REJECTED}>
              Reject
            </option>
          </Select>
          {errorMessageApproval && (
            <p style={{ fontSize: "14px", color: "red" }}>
              {errorMessageApproval}
            </p>
          )}
          {approvalStatus === "3" && (
            <>
              <p style={{ marginBottom: 5, fontWeight: "bold" }}>Note</p>
              <Input
                placeholder="Input your note here"
                onChange={(e) => handleChangeNote(e.target.value)}
                borderColor={errorMessageNote ? "red" : "gray"}
										/>
              {errorMessageNote && (
                <p style={{ fontSize: "14px", color: "red" }}>
                  {errorMessageNote}
                </p>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter
          style={{
									display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            gap: 10,
          }}
							>
								<Button type="button" disabled={formik.isSubmitting} onClick={handleCancel} borderColor="#D0D5DD" bgColor="white">
            Cancel
          </Button>
          <Button
									type="submit"
									isLoading={formik.isSubmitting}
									loadingText="Confirming"
									disabled={formik.isSubmitting}
            bgColor={COLORS.BUTTON.BACKGROUND}
            color={COLORS.BUTTON.FOREGROUND}
								>
            Confirm
          </Button>
        </ModalFooter>
      </ModalContent>
					</Form>
				)}
			</Formik>
    </Modal>
  );
};

export default ModalSireReviewerInvoiceConfirm;
